<template>
  <app-widget :loading="loading" :title="$t('common.devices')">
    <span
      slot="widget-header-action">
      <v-btn
        data-cy="newDeviceBtn"
        color="success"
        :to="routeConstants.NEW_DEVICE_URL">

        <v-icon small>
          add
        </v-icon>
        {{ $t('device.add') }}
      </v-btn>

      <v-btn
        data-cy="deviceUsageBtn"
        color="success"
        class="ml-2"
        :to="routeConstants.DEVICES_CALENDAR_URL">

        <v-icon left small>
          calendar_today
        </v-icon>
        {{ $t('device.deviceUsage') }}
      </v-btn>
    </span>

    <v-data-table
      slot="widget-content"
      :options.sync="options"
      :headers="headers"
      :items="devices"
      :server-items-length="devicesCount"
      must-sort
      :sort-by="(filter.sortBy && filter.sortBy.length) ? filter.sortBy[0] : null"
      :items-per-page="filter.take"
      :footer-props="{
        'items-per-page-text': $t('common.table.rowsPerPage'),
        'items-per-page-options': [10, 20, 30]
      }">

      <template v-slot:item="{ item }">
        <tr 
          data-cy="device-row"
          :data-cy-device-row="item.id"
          @click="onTableRowClicked(item)"
          :class="{ 
            active: selectedDevice && selectedDevice.id === item.id, 
            'clickable-row': item.lastLocation
          }">
          <td>{{ item.name }}</td>
          <td>{{ item.imei }}</td>
          <td>{{ $t(`device.statusType.${DEVICE_STATUS_TYPES_ARR[item.deviceStatus]}`) }}</td>
          <td>{{ $t(`device.type.${DEVICE_TYPE_ARR[item.type]}`) }}</td>
          <td>{{ item.lastMoveName || $t('common.notApplicable') }}</td>
          <td>
            <template v-if="item.batteryPercentage">
              {{ `${item.batteryPercentage}%` }}
            </template>
            <template v-else>
              {{ $t('common.notApplicable' )}}
            </template>
          </td>
          <td>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  data-cy="viewMoreBtn" 
                  small
                  v-on="on"
                  color="transparent">
                  <v-icon
                    small
                  >
                    more_horiz
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  data-cy="deviceDetailsBtn"
                  :to="routeConstants.DEVICE_DETAILS_URL.replace(':id', item.id)">
                  <v-list-item-icon class="mr-0">
                    <v-icon
                      small
                      color="success"
                    >
                      remove_red_eye
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('common.actions.view') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  data-cy="editDeviceBtn"
                  :to="routeConstants.EDIT_DEVICE_URL.replace(':id', item.id)">

                  <v-list-item-icon class="mr-0">
                    <v-icon
                      small
                      color="success"
                    >
                      edit
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('common.actions.edit') }}

                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item 
                  data-cy="deleteDeviceBtn"
                  @click="showDeleteConfirmationModal(item)">
                  <v-list-item-icon class="mr-0">
                    <v-icon
                      small
                      color="error"
                    >
                      delete
                    </v-icon>
                  </v-list-item-icon>
                  
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('common.actions.delete') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        {{ $t('common.noData') }}
      </template>
    </v-data-table>
  </app-widget>
</template>

<script>
import {  
  DEVICE_STATUS_TYPES_ARR,
  DEVICE_TYPE_ARR
} from '@/constants';
import * as routeConstants from '@/router/constants';
import AppWidget from '@/components/widgets/AppWidget';

export default {
  name: 'DevicesTable',
  props: {
    devices: Array,
    loading: Boolean,
    devicesCount: Number,
    filter: Object,
    selectedDevice: Object
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('device.fields.name'), 
          value: 'name', 
          width: "15%",
          class: "cy-devices-row-name"
        },
        {
          text: this.$t('device.fields.imei'), 
          value: 'imei', 
          sortable: false, 
          width: "15%"
        },
        {
          text: this.$t('device.fields.deviceStatusType'), 
          value: 'deviceStatus', 
          width: "15%"
        },
        {
          text: this.$t('device.fields.deviceType'), 
          value: 'type', 
          width: "15%"
        },
        {
          text: this.$t('device.fields.moveName'),
          value: 'lastMoveName',
          width: '15%'
        },
        {
          text: this.$t('device.fields.lastKnownBatteryLevel'),
          value: 'lastKnownBatteryLevel',
          width: "15%"
        },
        { 
          text: 'Actions', 
          value: 'action', 
          sortable: false,
          width: "10%"
        },
      ]
    }
  },
  data: () => ({
    DEVICE_STATUS_TYPES_ARR,
    DEVICE_TYPE_ARR,
    routeConstants,
    options: {}
  }),
  watch: {
    options: {
      handler (options) {
        const newFilter = { ...this.filter, ...options };

        this.$emit('updateFilter', newFilter);
      },
      deep: true,
    }
  },
  methods: {
    showDeleteConfirmationModal(device) {
      this.$emit('onDeleteDeviceRequest', device);
    },
    onTableRowClicked(device) {
      if(device.lastLocation) {
        this.$emit('onUpdateSelectedDevice', device);
      }
    }
  },
  components: {
    AppWidget
  }
}
</script>

<style lang="scss">
  .clickable-row {
    cursor: pointer;
  }
</style>